import React from 'react';
import { PageSEO } from '../PageSEO';
import { LegalPageLayout } from './LegalPageLayout';

/**
 * Capital Access Platform Terms — Phase 99A.2 light direction.
 *
 * Legal text preserved verbatim from the 20 March 2026 version. Only
 * chrome migrated to LegalPageLayout.
 */
export const PlatformTerms = () => (
  <>
    <PageSEO
      title="Platform Terms of Use — GP Capital"
      description="Terms governing access to the GP Capital invitation-only platform. Operated by GP CAP Pty Ltd (ACN 665 155 608)."
      path="/platform-terms"
      referrerPolicy="strict-origin"
    />
    <LegalPageLayout
      eyebrow="Platform Terms"
      titleLead="Capital Access Platform"
      titleAccent="terms of use."
      lastUpdated="Last updated: 20 March 2026"
      testId="platform-terms-page"
      relatedLinks={[
        { to: '/terms', label: 'Website Terms of Use', testId: 'terms' },
        { to: '/privacy', label: 'Privacy Policy', testId: 'privacy' },
        { to: '/disclosures', label: 'Disclosures', testId: 'disclosures' },
        { to: '/security', label: 'Security & Responsible Disclosure', testId: 'security' },
        { to: '/trust', label: 'Trust Center', testId: 'trust' },
      ]}
    >
      <p>
        These Capital Access Platform Terms (&quot;<strong>Platform Terms</strong>&quot;) govern your access to and use of GP Capital&apos;s Capital Access Platform (the &quot;<strong>Platform</strong>&quot;), a private, invitation-based deal management and capital markets environment. These Platform Terms are separate from and in addition to the Website Terms of Use and any deal-level confidentiality agreements.
      </p>
      <p>By logging in to or using the Platform, you agree to be bound by these Platform Terms.</p>

      <h2>1. Platform Operator</h2>
      <p>
        The Platform is operated by <strong>GP CAP Pty Ltd ACN 665 155 608</strong> (&quot;<strong>GP Capital</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;, &quot;<strong>our</strong>&quot;).
      </p>

      <h2>2. Invitation-Only Access</h2>
      <p>
        Access to the Platform is by invitation only. You may only access deals, documents and information that GP Capital has explicitly assigned to you. Unauthorised access to any part of the Platform, or attempts to access deals or information not assigned to your account, is strictly prohibited.
      </p>

      <h2>3. Confidential Information</h2>
      <p>
        All information accessible through the Platform, including but not limited to deal summaries, financial models, underwriting materials, sponsor information, borrower details, indicative terms and any other documents or data (&quot;<strong>Confidential Information</strong>&quot;), is strictly confidential. You must:
      </p>
      <ul>
        <li>Use Confidential Information solely for the purpose of evaluating the relevant deal opportunity;</li>
        <li>Not copy, reproduce, redistribute, forward, disclose or otherwise make available any Confidential Information to any person not authorised by GP Capital;</li>
        <li>Not use Confidential Information for any purpose other than evaluating the specific opportunity for which it was provided;</li>
        <li>Maintain appropriate safeguards to protect the confidentiality and security of all Confidential Information.</li>
      </ul>

      <h2>4. No Direct Contact or Circumvention</h2>
      <p>
        You must not contact, solicit or engage directly with any borrower, sponsor, co-lender or other party identified through the Platform without the prior written consent of GP Capital. Any attempt to circumvent GP Capital&apos;s role as introducer and intermediary is a material breach of these Platform Terms and may result in immediate termination of access and pursuit of available remedies.
      </p>

      <h2>5. Deal-Level Confidentiality Gates</h2>
      <p>
        Individual deals may require you to accept additional confidentiality restrictions, fee protection agreements or non-disclosure terms before accessing the deal room. These deal-level terms are in addition to these Platform Terms and the Website Terms of Use.
      </p>

      <h2>6. Indicative Information Only</h2>
      <p>
        All deal information, terms, pricing, projections and other materials provided through the Platform are <strong>indicative only</strong>. Nothing on the Platform constitutes an offer, commitment, undertaking or guarantee. All transactions are subject to:
      </p>
      <ul>
        <li>Satisfactory underwriting and due diligence;</li>
        <li>Applicable credit and investment committee approvals;</li>
        <li>Execution of definitive documentation acceptable to all parties;</li>
        <li>Compliance with all applicable laws and regulatory requirements.</li>
      </ul>

      <h2>7. No Reliance</h2>
      <p>
        You acknowledge that you are not relying on GP Capital, the Platform, or any information provided through the Platform for any investment, lending or other decision. You are responsible for conducting your own independent analysis, due diligence and assessment. GP Capital makes no representation or warranty regarding the accuracy, completeness or suitability of any information.
      </p>

      <h2>8. Activity Monitoring and Logging</h2>
      <p>
        All activity on the Platform is logged and monitored. This includes but is not limited to: logins, document views, document downloads, questions submitted, bids submitted, bid revisions, NDA acceptances and access events. These logs may be used for compliance, audit, dispute resolution and platform administration purposes.
      </p>

      <h2>9. Bid Submission and Process</h2>
      <p>
        Indicative bids submitted through the Platform are non-binding. GP Capital reserves the right to manage the bid process at its sole discretion, including setting deadlines, managing rounds, shortlisting counterparties and selecting outcomes. Bid visibility, revision rights and process structure are controlled by GP Capital on a deal-by-deal basis.
      </p>

      <h2>10. Termination of Access</h2>
      <p>
        GP Capital may suspend or terminate your Platform access at any time, with or without notice, for any reason including breach of these Platform Terms, breach of any deal-level confidentiality terms, or at GP Capital&apos;s sole discretion. Upon termination, all obligations regarding Confidential Information survive.
      </p>

      <h2>11. Relationship to Other Terms</h2>
      <p>These Platform Terms operate alongside:</p>
      <ul>
        <li>The GP Capital <a href="/terms">Website Terms of Use</a>;</li>
        <li>The GP Capital <a href="/privacy">Privacy Policy</a>;</li>
        <li>The GP Capital <a href="/disclosures">Disclosures</a>;</li>
        <li>Any deal-level confidentiality, NDA or fee protection agreements.</li>
      </ul>
      <p>
        In the event of conflict, deal-level terms prevail over these Platform Terms, and these Platform Terms prevail over the Website Terms of Use, in relation to Platform access and use.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Platform Terms are governed by the laws of New South Wales, Australia. You submit to the non-exclusive jurisdiction of the courts of New South Wales.
      </p>

      <p className="legal-footnote" data-testid="platform-terms-footnote">
        GP CAP Pty Ltd · ACN 665 155 608
      </p>
    </LegalPageLayout>
  </>
);

export default PlatformTerms;
